import { supabase } from "../supabase/SupabaseClient";
import { traerTodas } from "./paginar";
import { actual } from "./quien";

/* Los comprobantes que llegan por WhatsApp: facturas, remitos y presupuestos.
 *
 * ── CADA UNO VE LO QUE CARGÓ, Y ESO NO SE FILTRA ACÁ ────────────────────────
 *
 * Igna, 11/09: *«que el sector exista para todos, pero que cada uno pueda ver
 * lo que cargó cada uno, como si fuera privado»*.
 *
 * El recorte lo hace la RLS (`comprobantes_lectura` → `veo_el_comprobante`),
 * no este archivo. Escribirlo también acá sería una segunda regla de
 * visibilidad que el día que cambie una de las dos empieza a discrepar con la
 * otra — y la que gana es la de la base, así que la del front sólo puede
 * esconder de más.
 *
 * Lo que sí es de acá: que una lista vacía se lea como «vos no cargaste
 * ninguno» y no como «no hay comprobantes». Son dos cosas muy distintas y la
 * pantalla no puede distinguirlas sola.
 *
 * ── EL ARCHIVO ES EL COMPROBANTE ────────────────────────────────────────────
 *
 * `archivo` en null NO es «todavía no cargó»: es una fila cuyo papel se perdió.
 * Pasó con las 6 primeras (el bucket no existía hasta el 11/09). Se muestra
 * como lo que es, porque los campos leídos por un modelo sin el original al
 * lado no se pueden verificar contra nada.
 */

export const BUCKET = "comprobantes";

/* Una hora, igual que el visor de vales: el que abre la pantalla lo ve, y el
 * link pegado en un chat se muere solo. Un remito trae CUIT, importes y quién
 * retiró qué. */
export const VIGENCIA = 3600;

const CAMPOS =
  "id,tipo,proveedor,cuit,fecha,numero,importe,moneda,detalle," +
  "archivo,mime,dudas,leido_por,quien,quien_nombre,roles,alcance,creado_en," +
  "revisado_en,revisado_por,anulado_en,anulado_por";

/* Todas las que la base me deje ver, INCLUIDAS LAS DESCARTADAS.
 *
 * Antes se filtraban acá con `.is("anulado_en", null)` y no había forma de
 * volver a verlas: descartar una foto la borraba de la pantalla para siempre,
 * que es lo mismo que borrarla con otro nombre. La pantalla tiene una pestaña
 * para mirarlas y un botón para devolverlas al cajón — esconder no es cerrar.
 *
 *
 * `traerTodas` en vez de un `.limit()`: hoy son 6 y el tope no se toca, pero
 * el día que sean mil una lista cortada se vería igual que una completa — y lo
 * que faltaría son facturas. Es el mismo agujero que ya mordió en Presidencia
 * y en vacaciones.
 *
 * El orden desempata por `id` porque `fecha` empata todo el tiempo: un remito
 * y su factura llevan la misma, y paginar con un orden que empata repite una
 * fila y saltea otra. `nullsFirst: false` manda al fondo las que el modelo no
 * pudo fechar, que es donde se buscan.
 */
export function traerComprobantes() {
  return traerTodas(() =>
    supabase
      .from("comprobantes")
      .select(CAMPOS, { count: "exact" })
      .order("fecha", { ascending: false, nullsFirst: false })
      .order("id", { ascending: false }),
  );
}

/* El link firmado al papel. `null` es «no se pudo», y la pantalla lo dice.
 *
 * La policy de storage (`comprobantes: el archivo sigue a la fila`) resuelve el
 * permiso del objeto mirando la fila que lo nombra, así que acá no hay nada que
 * chequear: si la fila llegó, el archivo se firma.
 */
export async function archivoUrl(ruta) {
  if (!ruta) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(ruta, VIGENCIA);
  if (error) return null;
  return data?.signedUrl || null;
}


/* ── Corregir ───────────────────────────────────────────────────────────────
 *
 * Igna, 15/09: *«que se pueda "mandar" a donde corresponde al igual que
 * editarlo por si hubo algo que no leyó. Como con los vales»*.
 *
 * Lo que el modelo leyó y lo que una persona corrigió NO valen lo mismo, y la
 * fila lo dice: `revisado_en` y `revisado_por` se escriben en cada corrección.
 * Sin eso, dentro de un mes no hay forma de saber cuál de los dos campos miró
 * alguien con el papel en la mano.
 */

export const TIPOS_VALIDOS = ["factura", "remito", "presupuesto", "otro"];

/* Los campos que se pueden corregir desde la pantalla. `archivo`, `media_id`,
 * `quien` y `leido_por` NO están: son el rastro de cómo llegó el comprobante,
 * y una corrección no cambia eso. */
const EDITABLES = ["tipo", "proveedor", "cuit", "fecha", "numero", "importe",
                   "moneda", "detalle", "alcance"];

export async function corregir(id, campos, dudasPrevias = []) {
  if (!id) throw new Error("falta el comprobante");
  const parche = {};
  for (const k of EDITABLES) {
    if (Object.prototype.hasOwnProperty.call(campos, k)) parche[k] = campos[k];
  }
  if (!Object.keys(parche).length) return null;

  /* El tipo se valida ACÁ además de en la base. El CHECK ya lo rechaza, pero
   * ese error llega como un 400 de PostgREST y la pantalla lo mostraría como
   * «no se pudo guardar»: un typo se vería como una falla de red. */
  if (parche.tipo && !TIPOS_VALIDOS.includes(parche.tipo)) {
    throw new Error(`Tipo desconocido: ${parche.tipo}`);
  }

  /* UNA DUDA CORREGIDA DEJA DE SER UNA DUDA. `dudas` son los campos de los que
   * el modelo no estaba seguro; el que acaba de escribir ese campo mirando el
   * papel ya lo resolvió, y dejar el ⚠️ manda a revisar dos veces lo mismo.
   * Los que no se tocaron siguen marcados. */
  const tocados = Object.keys(parche);
  parche.dudas = (dudasPrevias || []).filter((d) => !tocados.includes(d));

  /* `revisado_por` viaja, pero el que vale lo pone la base con el mail de la
   * sesión (trigger `comprobantes_sellar_autor`, 20/10): el que manda el
   * cliente no sirve de auditoría. */
  const yo = actual();
  parche.revisado_en = new Date().toISOString();
  parche.revisado_por = yo?.mail || null;

  const { data, error } = await supabase
    .from("comprobantes").update(parche).eq("id", id).select("id");
  if (error) throw error;
  /* Cero filas ES el error: la RLS filtra en silencio. */
  if (!data || !data.length) {
    throw new Error("No se pudo guardar la corrección.");
  }
  return data[0];
}

/* «Esto no es un comprobante». NO borra: el archivo queda y la fila se puede
 * devolver al cajón. El papel es lo único que no se puede reponer, y el que
 * descarta hoy no es el que va a buscar esa foto en tres meses. */
export async function descartar(id, descartado = true) {
  const yo = actual();
  const { data, error } = await supabase
    .from("comprobantes")
    .update(descartado
      ? { anulado_en: new Date().toISOString(), anulado_por: yo?.mail || null }
      : { anulado_en: null, anulado_por: null })
    .eq("id", id)
    .select("id");
  if (error) throw error;
  if (!data || !data.length) {
    throw new Error("No se pudo guardar.");
  }
  return data[0];
}
